import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sparkle, Fire, FunnelSimple, X, UsersThree, PhoneCall, SealCheck, Trash, ClipboardText, UserPlus, WhatsappLogo, Info, CaretUp, CaretDown } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import AIDrawer, { useAIStream } from "@/components/AIDrawer";
import { SpeedToLead } from "@/components/SpeedToLead";
import { api } from "@/lib/api";

const ScoreBadge = ({ score }) => {
  const style = score >= 75 ? "bg-success/10 text-[#00953e] border-success/25"
    : score >= 50 ? "bg-warning/10 text-[#b07600] border-warning/30"
    : "bg-red-50 text-red-500 border-red-200";
  const label = score >= 75 ? "Hot" : score >= 50 ? "Warm" : "Cold";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${style}`}>
      {score >= 75 && <Fire size={11} weight="fill" />}{score} · {label}
    </span>
  );
};

const STATUS_STYLES = {
  new: "bg-[#0052FF]/8 text-[#0052FF] border-[#0052FF]/20",
  contacted: "bg-ai/10 text-ai border-ai/25",
  qualified: "bg-success/10 text-[#00953e] border-success/25",
  junk: "bg-[#F3F4F6] text-[#8a90a0] border-[#E5E7EB]",
};

const parseDate = (s) => new Date(String(s || "").replace("+0000", "+00:00"));
const fmtDT = (s) => {
  const d = parseDate(s);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short" }) + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

const FSelect = ({ value, onChange, options, all, width = "w-44", testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${width} h-9 text-xs font-semibold bg-white`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent className="max-h-72">
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const Stat = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]"><Icon size={13} weight="duotone" className={accent ? "text-[#FF6B35]" : "text-[#0052FF]"} /> {label}</div>
    <div className="mt-1.5 font-heading text-xl font-extrabold tracking-tighter text-[#0A0D14]">{value}</div>
    {sub && <div className="text-[11px] text-[#8a90a0] mt-0.5">{sub}</div>}
  </div>
);

export default function Leads() {
  const [leads, setLeads] = useState(null);
  const [totals, setTotals] = useState({ total: 0, hot: 0 });
  const [formNames, setFormNames] = useState({});
  const [waMap, setWaMap] = useState({});
  const [drawer, setDrawer] = useState(null);
  const ai = useAIStream();

  const [fCamp, setFCamp] = useState("all");
  const [fForm, setFForm] = useState("all");
  const [fAd, setFAd] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fQuality, setFQuality] = useState("all");
  const [fDays, setFDays] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [q, setQ] = useState("");
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    api.get("/leads").then(({ data }) => { setLeads(data.leads); setTotals({ total: data.total, hot: data.hot }); }).catch(() => setLeads([]));
    api.get("/forms").then(({ data }) => setFormNames(Object.fromEntries((data.forms || []).map((f) => [f.id, f.name])))).catch(() => {});
    api.get("/speedlead/log").then(({ data }) => setWaMap(Object.fromEntries((data.messages || []).map((m) => [m.lead_id, m.status])))).catch(() => {});
  }, []);

  const campaigns = useMemo(() => [...new Set((leads || []).map((l) => l.campaign_name).filter(Boolean))].sort(), [leads]);
  const forms = useMemo(() => [...new Set((leads || []).map((l) => l.form_id).filter(Boolean))], [leads]);
  const ads = useMemo(() => [...new Set((leads || []).map((l) => l.ad_name).filter(Boolean))].sort(), [leads]);
  const formName = (id) => (id ? (formNames[id] || `Form …${String(id).slice(-4)}`) : "—");

  const dateRange = useMemo(() => {
    const now = new Date();
    const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monday = new Date(sod.getTime() - ((sod.getDay() + 6) % 7) * 86400000);
    if (fDays === "all") return [null, null];
    if (fDays === "today") return [sod, null];
    if (fDays === "yesterday") return [new Date(sod.getTime() - 86400000), sod];
    if (fDays === "this_week") return [monday, null];
    if (fDays === "prev_week") return [new Date(monday.getTime() - 7 * 86400000), monday];
    if (fDays === "this_month") return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    if (fDays === "prev_month") return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    if (fDays === "custom") return [fFrom ? new Date(fFrom) : null, fTo ? new Date(new Date(fTo).getTime() + 86400000) : null];
    return [new Date(Date.now() - Number(fDays) * 86400000), null];
  }, [fDays, fFrom, fTo]);

  const filtered = useMemo(() => {
    if (!leads) return null;
    const [start, end] = dateRange;
    const s = q.toLowerCase();
    return leads.filter((l) => {
      const d = parseDate(l.created_at);
      return (fCamp === "all" || l.campaign_name === fCamp) &&
        (fForm === "all" || l.form_id === fForm) &&
        (fAd === "all" || l.ad_name === fAd) &&
        (fStatus === "all" || l.status === fStatus) &&
        (fQuality === "all" || (fQuality === "hot" ? l.quality_score >= 75 : fQuality === "warm" ? l.quality_score >= 50 && l.quality_score < 75 : l.quality_score < 50)) &&
        (!start || d >= start) && (!end || d < end) &&
        (!s || (l.name || "").toLowerCase().includes(s) || (l.course || "").toLowerCase().includes(s) || (l.city || "").toLowerCase().includes(s) || (l.phone || "").includes(q));
    });
  }, [leads, fCamp, fForm, fAd, fStatus, fQuality, dateRange, q]);

  const sorted = useMemo(() => {
    if (!filtered) return null;
    return [...filtered].sort((a, b) => (parseDate(a.created_at) - parseDate(b.created_at)) * (sortDir === "asc" ? 1 : -1));
  }, [filtered, sortDir]);

  const stats = useMemo(() => {
    const f = filtered || [];
    return {
      total: f.length,
      hot: f.filter((l) => l.quality_score >= 75).length,
      fresh: f.filter((l) => l.status === "new").length,
      contacted: f.filter((l) => l.status === "contacted").length,
      qualified: f.filter((l) => l.status === "qualified").length,
      junk: f.filter((l) => l.status === "junk").length,
    };
  }, [filtered]);

  const activeFilters = [fCamp, fForm, fAd, fStatus, fQuality].filter((f) => f !== "all").length + (fDays !== "all" ? 1 : 0) + (q ? 1 : 0);
  const clearAll = () => { setFCamp("all"); setFForm("all"); setFAd("all"); setFStatus("all"); setFQuality("all"); setFDays("all"); setFFrom(""); setFTo(""); setQ(""); };
  const daysLabel = { all: "all time", 7: "last 7 days", 14: "last 14 days", 30: "last 30 days",
                      today: "today", yesterday: "yesterday", this_week: "this week", prev_week: "previous week",
                      this_month: "this month", prev_month: "previous month", custom: "custom range" }[fDays] || "all time";

  const changeStatus = async (lead, status) => {
    try {
      await api.patch(`/leads/${lead.id}/status`, { status });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, status } : l)));
      toast.success(`${lead.name} marked as ${status}`);
    } catch {
      toast.error("Failed to update lead status");
    }
  };

  const prioritize = () => {
    setDrawer({ title: "Lead Quality Analysis", subtitle: "Prioritized follow-up plan · junk lead patterns · form improvements" });
    ai.start({ type: "leads" });
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="leads-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0A0D14] mr-1"><FunnelSimple size={15} weight="bold" /> Filters</span>
          <FSelect value={fCamp} onChange={setFCamp} all="All campaigns" options={campaigns.map((c) => ({ v: c, l: c }))} width="w-52" testId="leads-filter-campaign" />
          <FSelect value={fForm} onChange={setFForm} all="All Instant Forms" options={forms.map((f) => ({ v: f, l: formName(f) }))} width="w-44" testId="leads-filter-form" />
          <FSelect value={fAd} onChange={setFAd} all="All ads" options={ads.map((a) => ({ v: a, l: a }))} width="w-48" testId="leads-filter-ad" />
          <FSelect value={fStatus} onChange={setFStatus} all="All statuses"
            options={["new", "contacted", "qualified", "junk"].map((s) => ({ v: s, l: s[0].toUpperCase() + s.slice(1) }))} width="w-36" testId="leads-filter-status" />
          <FSelect value={fQuality} onChange={setFQuality} all="Any quality"
            options={[{ v: "hot", l: "🔥 Hot (75+)" }, { v: "warm", l: "Warm (50-74)" }, { v: "cold", l: "Cold (<50)" }]} width="w-36" testId="leads-filter-quality" />
          <FSelect value={fDays} onChange={setFDays} all="All time"
            options={[{ v: "today", l: "Today" }, { v: "yesterday", l: "Yesterday" },
                      { v: "this_week", l: "This week" }, { v: "prev_week", l: "Previous week" },
                      { v: "7", l: "Last 7 days" }, { v: "14", l: "Last 14 days" }, { v: "30", l: "Last 30 days" },
                      { v: "this_month", l: "This month" }, { v: "prev_month", l: "Previous month" }, { v: "custom", l: "Custom range…" }]}
            width="w-40" testId="leads-filter-days" />
          {fDays === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="leads-custom-date-range">
              <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="leads-filter-date-from"
                className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
              <span className="text-[11px] text-[#8a90a0] font-bold">to</span>
              <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="leads-filter-date-to"
                className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
            </div>
          )}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, course, city…"
            data-testid="leads-search-input" className="h-9 w-56 text-xs bg-white" />
          {activeFilters > 0 && (
            <button onClick={clearAll} className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[11px] font-bold text-[#525866] px-3 py-1.5 hover:bg-[#e7e9ee]" data-testid="leads-filter-clear">
              <X size={11} weight="bold" /> Clear ({activeFilters})
            </button>
          )}
          <div className="ml-auto flex items-center gap-2">
            <SpeedToLead />
            <Button onClick={prioritize} data-testid="leads-ai-button" size="sm"
              className="rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold h-9">
              <Sparkle size={14} weight="fill" className="mr-1.5" /> AI Prioritize
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="leads-stat-cards">
        <Stat icon={UsersThree} label="Leads in view" value={stats.total.toLocaleString("en-IN")} sub={daysLabel} testId="leads-stat-total" />
        <Stat icon={Fire} label="Hot" value={stats.hot.toLocaleString("en-IN")} sub="quality 75+" accent testId="leads-stat-hot" />
        <Stat icon={UserPlus} label="New" value={stats.fresh.toLocaleString("en-IN")} sub="not contacted yet" testId="leads-stat-new" />
        <Stat icon={PhoneCall} label="Contacted" value={stats.contacted.toLocaleString("en-IN")} sub="in follow-up" testId="leads-stat-contacted" />
        <Stat icon={SealCheck} label="Qualified" value={stats.qualified.toLocaleString("en-IN")} sub="sales-ready" accent testId="leads-stat-qualified" />
        <Stat icon={Trash} label="Junk" value={stats.junk.toLocaleString("en-IN")} sub={stats.total ? `${Math.round((stats.junk / stats.total) * 100)}% of view` : "—"} testId="leads-stat-junk" />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-x-auto">
        {!filtered ? (
          <div className="p-6 space-y-3">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-11 rounded-md" />)}</div>
        ) : (
          <Table className="min-w-[1000px]">
            <TableHeader>
              <TableRow className="border-b border-[#E5E7EB] hover:bg-transparent">
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Lead</TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">
                  <button onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))} data-testid="leads-sort-created"
                    className="inline-flex items-center gap-1 uppercase tracking-[0.15em] font-bold hover:text-[#0A0D14] transition-colors"
                    title={sortDir === "desc" ? "Newest first — click for oldest first" : "Oldest first — click for newest first"}>
                    Created time {sortDir === "desc" ? <CaretDown size={11} weight="bold" /> : <CaretUp size={11} weight="bold" />}
                  </button>
                </TableHead>
                {["Course · City", "Campaign", "Instant Form", "Ad"].map((h) => (
                  <TableHead key={h} className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">{h}</TableHead>
                ))}
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">
                  <span className="inline-flex items-center gap-1">
                    Quality
                    <TooltipProvider delayDuration={100}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button data-testid="leads-quality-info" className="text-[#8a90a0] hover:text-[#0052FF] transition-colors" aria-label="How quality is scored">
                            <Info size={13} weight="bold" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[290px] bg-[#0A0D14] text-white p-3 normal-case tracking-normal">
                          <p className="font-bold mb-1.5">How the quality score works</p>
                          <ul className="space-y-1 text-[11px] leading-relaxed">
                            <li>• Base <b>40</b> — lead submitted the form</li>
                            <li>• <b>+15</b> — provided an email</li>
                            <li>• <b>+20</b> — provided a phone number</li>
                            <li>• <b>+10</b> — fresh lead (created within 48h of sync)</li>
                            <li>• Capped at <b>95</b></li>
                          </ul>
                          <p className="mt-1.5 text-[11px] text-white/70">e.g. 85 = email + phone + fresh · 75 = email + phone · 65 = email + fresh (no phone)</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </span>
                </TableHead>
                <TableHead className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-xs text-[#8a90a0] py-8" data-testid="leads-empty">No leads match these filters.</TableCell></TableRow>
              ) : sorted.slice(0, 300).map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`} className="border-b border-[#E5E7EB]/40 hover:bg-[#FAFAFB]">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span className="h-8 w-8 shrink-0 rounded-full bg-[#0A0D14] text-white text-[10px] font-bold flex items-center justify-center">
                        {(l.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}
                      </span>
                      <div className="min-w-0">
                        <div className="font-semibold text-sm text-[#0A0D14] max-w-[150px] truncate" title={l.name}>{l.name}</div>
                        <div className="text-[11px] font-mono text-[#8a90a0] flex items-center gap-1">
                          {l.phone}
                          {waMap[l.id] && (
                            <WhatsappLogo size={13} weight="fill" data-testid={`lead-wa-badge-${l.id}`}
                              className={waMap[l.id] === "sent" ? "text-[#25D366]" : "text-[#c73030]"}
                              title={waMap[l.id] === "sent" ? "WhatsApp auto-reply sent" : "WhatsApp send failed"} />
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-xs text-[#525866] whitespace-nowrap">{fmtDT(l.created_at)}</TableCell>
                  <TableCell>
                    <div className="text-xs font-semibold text-[#3a4150]">{l.course || "—"}</div>
                    <div className="text-[10px] text-[#8a90a0]">{l.city || "—"}</div>
                  </TableCell>
                  <TableCell><div className="max-w-[180px] text-xs text-[#525866] truncate" title={l.campaign_name}>{l.campaign_name}</div></TableCell>
                  <TableCell>
                    <div className="max-w-[160px] flex items-center gap-1 text-[11px] text-[#525866]" title={formName(l.form_id)}>
                      <ClipboardText size={12} className="text-[#FF6B35] shrink-0" /><span className="truncate">{formName(l.form_id)}</span>
                    </div>
                  </TableCell>
                  <TableCell><div className="max-w-[160px] text-[11px] text-[#525866] truncate" title={l.ad_name}>{l.ad_name || "—"}</div></TableCell>
                  <TableCell><ScoreBadge score={l.quality_score} /></TableCell>
                  <TableCell>
                    <Select value={l.status} onValueChange={(v) => changeStatus(l, v)}>
                      <SelectTrigger data-testid={`lead-status-select-${l.id}`}
                        className={`h-7 w-[120px] rounded-full text-[11px] font-bold uppercase border px-3 ${STATUS_STYLES[l.status] || STATUS_STYLES.new}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["new", "contacted", "qualified", "junk"].map((s) => (
                          <SelectItem key={s} value={s} className="text-xs font-semibold uppercase">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {filtered && filtered.length > 300 && (
        <p className="text-[11px] text-[#8a90a0]">Showing first 300 of {filtered.length} — narrow the filters to see specific leads. Total captured: {totals.total.toLocaleString("en-IN")}.</p>
      )}

      <AIDrawer open={!!drawer} onClose={() => { setDrawer(null); ai.reset(); }} title={drawer?.title} subtitle={drawer?.subtitle} text={ai.text} running={ai.running} error={ai.error} />
    </div>
  );
}
